/**
 * L-M4 U1 (D-924): dequantize `KHR_mesh_quantization` meshes at load. gltfpack stores positions as a u16 grid and puts
 * the dequantization scale and offset on the mesh node (or on the group GLTFLoader makes for a multi-primitive mesh), so
 * `positionLocal` in the hull shader was the raw grid while every metre constant (radius, pitch, scorch) stayed in
 * metres. This folds that transform into f32 geometry and resets the node(s) to identity, so mesh-local space is asset
 * metres. f32 meshes are left untouched (value-identical). Load time only, never per frame.
 */
import { BufferAttribute, Matrix3, Matrix4, Vector3, type BufferGeometry, type InterleavedBufferAttribute, type Mesh, type Object3D } from 'three/webgpu'

type Attr = BufferAttribute | InterleavedBufferAttribute

/** True when the mesh's position attribute is an integer (quantized) format. */
export function isQuantized(geometry: BufferGeometry): boolean {
  const pos = geometry.getAttribute('position') as Attr | undefined
  return pos !== undefined && !(pos.array instanceof Float32Array)
}

function sameMatrix(a: Matrix4, b: Matrix4): boolean {
  for (let i = 0; i < 16; i++) if (Math.abs(a.elements[i] - b.elements[i]) > 1e-9) return false
  return true
}

const v = new Vector3()

/** Bytes of the arrays behind `replaced` that nothing else in the geometry still reads (an interleaved buffer counts once). */
function sourceBytes(geometry: BufferGeometry, replaced: readonly Attr[]): number {
  const arrayOf = (a: Attr): ArrayLike<number> & { byteLength: number } => ('isInterleavedBufferAttribute' in a && a.isInterleavedBufferAttribute ? a.data.array : a.array) as ArrayLike<number> & { byteLength: number }
  const kept = new Set<unknown>()
  for (const a of Object.values(geometry.attributes)) if (!replaced.includes(a as Attr)) kept.add(arrayOf(a as Attr))
  const freed = new Set<ArrayLike<number> & { byteLength: number }>()
  for (const a of replaced) if (!kept.has(arrayOf(a))) freed.add(arrayOf(a))
  let bytes = 0
  for (const arr of freed) bytes += arr.byteLength
  return bytes
}

/** Replaces position (and normal, if quantized) with f32 attributes transformed by `m`. Returns the added bytes. */
function bake(geometry: BufferGeometry, m: Matrix4): number {
  const pos = geometry.getAttribute('position') as Attr
  const nrm0 = geometry.getAttribute('normal') as Attr | undefined
  const before = sourceBytes(geometry, nrm0 && !(nrm0.array instanceof Float32Array) ? [pos, nrm0] : [pos])
  const out = new Float32Array(pos.count * 3)
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i).applyMatrix4(m)
    out[i * 3] = v.x
    out[i * 3 + 1] = v.y
    out[i * 3 + 2] = v.z
  }
  geometry.setAttribute('position', new BufferAttribute(out, 3))
  let added = out.byteLength - before
  const nrm = geometry.getAttribute('normal') as Attr | undefined
  if (nrm && !(nrm.array instanceof Float32Array)) {
    const nm = new Matrix3().getNormalMatrix(m)
    const n = new Float32Array(nrm.count * 3)
    for (let i = 0; i < nrm.count; i++) {
      v.fromBufferAttribute(nrm, i).applyMatrix3(nm).normalize()
      n[i * 3] = v.x
      n[i * 3 + 1] = v.y
      n[i * 3 + 2] = v.z
    }
    added += n.byteLength
    geometry.setAttribute('normal', new BufferAttribute(n, 3))
  }
  geometry.computeBoundingBox()
  geometry.computeBoundingSphere()
  return added
}

/**
 * Dequantizes every quantized mesh under each `levels` node in place: the mesh's transform relative to its level is
 * folded into its geometry and the nodes between are reset to identity. A geometry shared under the same transform is
 * baked once (into a copy); under a different one it gets its own copy. Returns the vertex bytes added (f32 minus the replaced ints).
 */
export function dequantizeLevels(levels: readonly Object3D[]): number {
  const baked = new Map<BufferGeometry, { geometry: BufferGeometry; m: Matrix4 }>()
  const toReset = new Set<Object3D>()
  const levelInverse = new Matrix4()
  let added = 0
  for (const level of levels) {
    level.updateMatrixWorld(true)
    levelInverse.copy(level.matrixWorld).invert()
    const meshes: Mesh[] = []
    level.traverse((o) => {
      if ((o as Mesh).isMesh && isQuantized((o as Mesh).geometry)) meshes.push(o as Mesh)
    })
    for (const mesh of meshes) {
      const m = new Matrix4().multiplyMatrices(levelInverse, mesh.matrixWorld)
      const prior = baked.get(mesh.geometry)
      if (prior && sameMatrix(prior.m, m)) {
        mesh.geometry = prior.geometry
      } else {
        // Bake a copy: the source stays the quantized original, so a second transform can still read it.
        const source = mesh.geometry
        const g = source.clone()
        added += bake(g, m)
        if (!prior) baked.set(source, { geometry: g, m })
        mesh.geometry = g
      }
      for (let p: Object3D | null = mesh; p && p !== level; p = p.parent) toReset.add(p)
    }
  }
  for (const o of toReset) {
    o.position.set(0, 0, 0)
    o.quaternion.identity()
    o.scale.set(1, 1, 1)
    o.updateMatrix()
  }
  for (const level of levels) level.updateMatrixWorld(true)
  return added
}
